/**
 * feedbackDashboardApi.js — Admin-token-gated reads against the
 * site-feedback API. Used by the /admin/feedback dashboard page.
 *
 * The token is stored in localStorage under FEEDBACK_ADMIN_TOKEN_KEY
 * (entered by the admin once, then reused). A 401 response clears the
 * token so the page falls back to the prompt screen.
 *
 * Backend reference: server/app/routes/siteFeedbackRoute.js
 *   - GET /api/site-feedback/stats   (aggregates for dashboard)
 *   - GET /api/site-feedback         (raw listing, for fallback)
 *
 * Created 2026-05-03 (Sunday polish round 2).
 */

import axios from 'axios';

const API_BASE =
  (process.env.REACT_APP_API_BASE || 'http://localhost:5050') + '/api/site-feedback';

export const FEEDBACK_ADMIN_TOKEN_KEY = 'lg_feedback_admin_token';

/** Read the admin token from localStorage (or null). */
export const getAdminToken = () => {
  try {
    return localStorage.getItem(FEEDBACK_ADMIN_TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

/** Persist the admin token. */
export const setAdminToken = (token) => {
  try {
    localStorage.setItem(FEEDBACK_ADMIN_TOKEN_KEY, token);
  } catch {
    /* ignore — private mode */
  }
};

/** Clear the admin token (after logout or 401). */
export const clearAdminToken = () => {
  try {
    localStorage.removeItem(FEEDBACK_ADMIN_TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Fetch dashboard stats. Returns:
 *   { success: true, data: <stats> }
 * or
 *   { success: false, error: <msg>, status: <code>, unauthorized: true|false }
 */
export const fetchFeedbackStats = async () => {
  const token = getAdminToken();
  if (!token) {
    return { success: false, error: 'No admin token set.', unauthorized: true };
  }
  try {
    const res = await axios.get(`${API_BASE}/stats`, {
      headers: { 'x-admin-token': token }
    });
    return { success: true, data: res.data };
  } catch (err) {
    const status = err?.response?.status;
    const apiError = err?.response?.data;
    if (status === 401) {
      clearAdminToken();
    }
    return {
      success: false,
      error:
        apiError?.details ||
        apiError?.error ||
        err?.message ||
        'Failed to fetch stats.',
      status,
      unauthorized: status === 401
    };
  }
};

/**
 * Fetch raw feedback list (paginated by limit). Useful for the
 * "All entries" tab. Same auth/error contract as fetchFeedbackStats.
 */
export const fetchFeedbackList = async ({ limit = 50 } = {}) => {
  const token = getAdminToken();
  if (!token) {
    return { success: false, error: 'No admin token set.', unauthorized: true };
  }
  try {
    const res = await axios.get(API_BASE, {
      params: { limit },
      headers: { 'x-admin-token': token }
    });
    return { success: true, data: res.data };
  } catch (err) {
    const status = err?.response?.status;
    const apiError = err?.response?.data;
    if (status === 401) {
      clearAdminToken();
    }
    return {
      success: false,
      error:
        apiError?.details ||
        apiError?.error ||
        err?.message ||
        'Failed to fetch feedback list.',
      status,
      unauthorized: status === 401
    };
  }
};
